/* eslint-disable import/no-extraneous-dependencies */
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ResumeData } from './types';
import API from '../../network/index';

export const extractLastDigit = (jobSlug: string) => {
  const lastDigit = jobSlug.match(/\d+$/);
  return lastDigit ? lastDigit[0] : null;
};

export const getJobOrderId = (jobSlug: string) => {
  const lastDigit = extractLastDigit(jobSlug);
  return lastDigit ? lastDigit[0] : null;
};

export function excludeTempEmails(value: string) {
  const restrictedDomains: string[] = [
    'yopmail.com',
  ];
  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  const domain = value?.split('@')[1];

  if (!value || !domain) {
    return true;
  }

  if (restrictedDomains.includes(domain)) {
    return false;
  }

  if (!emailRegex.test(value)) {
    return false;
  }

  return true;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const uploadResume = async (
  file: File,
  orgId: number,
): Promise<{ response: ResumeData | null; resumeName: string; error: string }> => {
  if (!file) return { response: null, resumeName: '', error: 'Empty file' };

  const resumeName = file.name;
  const resumeType = resumeName.split('.').pop()?.toLowerCase();
  const supportedVersions = ['doc', 'docx', 'txt', 'rtf', 'pdf'];

  if (!supportedVersions.includes(resumeType || '')) {
    return { response: null, resumeName, error: 'Please upload a valid resume' };
  }

  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('s3FolderName', 'test');
    formData.append('userId', 'demo-test-user');
    formData.append('fileExtension', resumeType || '');
    formData.append('organizationId', orgId.toString());
    formData.append('isLocationRequired', 'true');
    formData.append('isHtmlLinkRequired', 'true');

    const response = await API.getInstance().uploadResume(formData);
    return { response, resumeName, error: '' };
  } catch (error) {
    return { response: null, resumeName, error: 'Failed to upload resume' };
  }
};

export const stripHtml = (html: string) => {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

export const applyJob = async (
  formValues: any,
  resumeData: ResumeData,
  orgId: number,
  jobOrderId: number,
): Promise<{ candidateId: string | null }> => {
  const data = {
    ...formValues,
    fileUrl: resumeData.s3DownloadLink,
    resumeHtmlUrl: resumeData.s3HtmlLink,
    organizationId: `${orgId}`,
    jobOrderId: `${jobOrderId}`,
    candidateStatus: 'APPLIED FROM WEBSITE',
  };

  const response = await API.getInstance().candidateApply(data);
  if (response?.status && response?.status === 'duplicate') {
    throw new Error('Candidate already applied');
  }
  return { candidateId: response.candidate?.hashKey || null };
};
